import { Logger } from "../core/Logger.js";

/**
 *
 */
class KeyboardInputInternal {
  constructor() {
    this.keys = new Set(); // Set to keep track of currently pressed keys
    this.keyPressTimes = new Map(); // Map to keep track of when each key was pressed
    this.keyReleaseTimes = new Map(); // Map to keep track of when each key was released
    this.keyUpNotified = new Map(); // Map to track if the key up event has been notified
    this.keyPressIntervals = new Map(); // Map to keep track of the start time of the current interval
  }

  // Initialize event listeners
  startCapture() {
    if (typeof window === 'undefined') {
      Logger.warn('Window is not defined. Event listeners cannot be registered.');
      return;
    }

    this.keydownHandler = event => this.handleKeyDown(event);
    window.addEventListener('keydown', this.keydownHandler);
    this.keyupHandler = event => this.handleKeyUp(event);
    window.addEventListener('keyup', this.keyupHandler);
  }

  // Handle keydown event
  handleKeyDown(event) {
    const keyCode = event.code;
    if (!this.keys.has(keyCode)) {
      this.keys.add(keyCode);
      this.keyPressTimes.set(keyCode, Date.now()); // Record the time the key was pressed
      this.keyReleaseTimes.delete(keyCode); // Ensure any previous release time is removed
      this.keyUpNotified.delete(keyCode); // Ensure key up notification is reset
      this.keyPressIntervals.set(keyCode, Date.now()); // Reset interval start time
    }
  }

  // Handle keyup event
  handleKeyUp(event) {
    const keyCode = event.code;
    if (this.keys.has(keyCode)) {
      this.keys.delete(keyCode); // Remove the key from the set
      this.keyPressTimes.delete(keyCode); // Remove the key press time
      this.keyReleaseTimes.set(keyCode, Date.now()); // Record the time the key was released
      this.keyUpNotified.set(keyCode, false); // Reset the notification status
      this.keyPressIntervals.delete(keyCode); // Remove interval tracking
    }
  }

  // Check if a specific key is pressed
  isKeyPressed(key) {
    return this.keys.has(key);
  }

  // Get the state of all currently pressed keys
  getPressedKeys() {
    return Array.from(this.keys);
  }

  // Check if a key has been pressed for a specific duration
  // The check resets after every interval
  isKeyPressedFor(key, durationInSeconds) {
    if (!this.keys.has(key)) {
      return false; // Key is not currently pressed
    }

    const intervalStartTime = this.keyPressIntervals.get(key);
    if (!intervalStartTime) {
      return false; // No record of when the interval started
    }

    const elapsedTime = (Date.now() - intervalStartTime) / 1000; // Convert milliseconds to seconds
    if (elapsedTime >= durationInSeconds) {
      // Reset interval start time for the next interval
      this.keyPressIntervals.set(key, Date.now());
      return true;
    }

    return false;
  }

  // Check if a key is currently not pressed and notify only once when the key is released
  isKeyReleased(key) {
    const isUp = !this.keys.has(key) && this.keyReleaseTimes.has(key);

    if (isUp) {
      const hasNotified = this.keyUpNotified.get(key);
      if (!hasNotified) {
        this.keyUpNotified.set(key, true); // Mark as notified
        return true;
      }
    }

    return false;
  }

  // Cleanup event listeners
  stopCapture() {
    this.keys.clear();
    this.keyPressTimes.clear();
    this.keyReleaseTimes.clear();
    this.keyUpNotified.clear();
    this.keyPressIntervals.clear();
    window.removeEventListener('keydown', this.keydownHandler);
    window.removeEventListener('keyup', this.keyupHandler);
  }
}

/**
 * Provides a static interface for capturing and managing keyboard input events.
 * It allows you to detect when keys are pressed, track currently pressed keys,
 * and check if a key has been pressed for a specified duration.
 */
export class KeyboardInput {
  static _keyboardInput = new KeyboardInputInternal();

  /**
   * Starts capturing keyboard input.
   */
  static startCapture() {
    KeyboardInput._keyboardInput.startCapture();
  }

  /**
   * Stops capturing keyboard input.
   */
  static stopCapture() {
    KeyboardInput._keyboardInput.stopCapture();
  }

  /**
   * Returns an array of currently pressed key codes.
   */
  static getPressedKeys() {
    return KeyboardInput._keyboardInput.getPressedKeys();
  }

  /**
   * Returns true only once if the key was previously pressed and then released.
   * @param {*} key The key code to check.
   */
  static isKeyReleased(key) {
    return KeyboardInput._keyboardInput.isKeyReleased(key);
  }

  /**
   * Checks if a specific key is currently pressed.
   * @param {*} key The key code to check.
   */
  static isKeyPressed(key) {
    return KeyboardInput._keyboardInput.isKeyPressed(key);
  }
  /**
   * Checks if a specific key has been pressed for a specified duration
   * @param {*} key The key code to check.
   * @param {*} durationInSeconds The duration in seconds.
   */
  static isKeyPressedFor(key, durationInSeconds) {
    return KeyboardInput._keyboardInput.isKeyPressed(key, durationInSeconds);
  }
}
